import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { htmlSafe } from '@ember/template';
import { arcPath } from '../../utils/charts/svg-utils.ts';
import { DEFAULT_CHART_COLORS } from '../../utils/charts/color-palette.ts';
import { createDebouncedResizeObserver } from '../../utils/charts/resize-observer-utils.ts';
import type { ChartDataPoint, ChartOptions } from '../../types/charts.ts';

// ---------------------------------------------------------------------------
// PieChart – pure SVG pie chart (full circle, no inner radius)
// ---------------------------------------------------------------------------

interface PieChartSignature {
  Args: ChartOptions & {
    /** Chart data points */
    data: ChartDataPoint[];
  };
  Element: HTMLDivElement;
}

interface PieSegment {
  path: string;
  color: string;
  data: ChartDataPoint;
  percent: number;
}

const MIN_SIZE = 160;

export default class PieChart extends Component<PieChartSignature> {
  @tracked containerWidth = 300;
  @tracked containerHeight = 300;
  @tracked hoveredIndex: number | null = null;

  get colors() {
    return this.args.colors ?? DEFAULT_CHART_COLORS;
  }

  get total() {
    return this.args.data.reduce((sum, d) => sum + d.value, 0) || 1;
  }

  get size() {
    const usable = Math.min(this.containerWidth, this.containerHeight);
    return Math.max(MIN_SIZE, usable);
  }

  get outerRadius() {
    return (this.size - 4) / 2;
  }

  get cx() {
    return this.size / 2;
  }

  get cy() {
    return this.size / 2;
  }

  get viewBox() {
    return `0 0 ${this.size} ${this.size}`;
  }

  get segments(): PieSegment[] {
    const { data } = this.args;
    if (!data.length) return [];

    let currentAngle = 0;
    return data.map((d, i) => {
      const fraction = d.value / this.total;
      const startAngle = currentAngle;
      const endAngle = currentAngle + fraction * Math.PI * 2 - 0.005;
      currentAngle += fraction * Math.PI * 2;

      return {
        path: arcPath({
          cx: this.cx,
          cy: this.cy,
          outerRadius: this.outerRadius,
          innerRadius: 0,
          startAngle,
          endAngle: Math.max(endAngle, startAngle + 0.01),
        }),
        color: this.colors[i % this.colors.length]!,
        data: d,
        percent: Math.round(fraction * 100),
      };
    });
  }

  get showLegend() {
    return this.args.showLegend !== false;
  }

  get legendItems() {
    return this.args.data.map((d, i) => ({
      label: d.category,
      color: this.colors[i % this.colors.length]!,
      value: d.value,
      percent: Math.round((d.value / this.total) * 100),
    }));
  }

  get svgStyle() {
    return htmlSafe(`width: ${this.size}px; height: ${this.size}px;`);
  }

  installObserver = modifier((element: HTMLElement) => {
    const observer = createDebouncedResizeObserver((entry) => {
      this.containerWidth = entry.contentRect.width;
      this.containerHeight = Math.max(MIN_SIZE, entry.contentRect.height || MIN_SIZE);
    });
    observer.observe(element);
    this.containerWidth = element.clientWidth || 300;
    this.containerHeight = Math.max(MIN_SIZE, element.clientHeight || MIN_SIZE);

    return () => observer.disconnect();
  });

  handleSegmentClick = (segment: PieSegment) => {
    this.args.onClick?.(segment.data);
  };

  setHovered = (index: number) => {
    this.hoveredIndex = index;
  };

  clearHovered = () => {
    this.hoveredIndex = null;
  };

  <template>
    <div
      data-test-chart
      data-test-chart-type="pie"
      class="flex flex-col items-center gap-4"
      ...attributes
    >
      <div class="tio-flex-center relative w-full" {{this.installObserver}}>
        <svg
          role="img"
          aria-label="Pie chart"
          style={{this.svgStyle}}
          viewBox={{this.viewBox}}
          class="overflow-visible"
        >
          {{#each this.segments as |seg i|}}
            <path
              data-test-chart-segment
              d={{seg.path}}
              fill={{seg.color}}
              class="transition-transform duration-150 origin-center cursor-pointer"
              style={{if
                (isEqual this.hoveredIndex i)
                (htmlSafe "transform: scale(1.03); filter: brightness(1.1);")
                (htmlSafe "")
              }}
              role="button"
              aria-label="{{seg.data.category}}: {{seg.data.value}} ({{seg.percent}}%)"
              {{on "mouseenter" (fn this.setHovered i)}}
              {{on "mouseleave" this.clearHovered}}
              {{on "click" (fn this.handleSegmentClick seg)}}
            />
          {{/each}}
        </svg>
      </div>

      {{#if this.showLegend}}
        <div data-test-chart-legend class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {{#each this.legendItems as |item|}}
            <div class="flex items-center gap-1.5">
              <span
                class="inline-block w-3 h-3 rounded-full shrink-0"
                style={{htmlSafe (concat "background-color: " item.color ";")}}
              ></span>
              <span class="tio-text-default font-medium truncate">{{item.label}}</span>
              <span class="tio-text-muted shrink-0">({{item.percent}}%)</span>
            </div>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

function isEqual(a: unknown, b: unknown) {
  return a === b;
}

function concat(...args: (string | number)[]) {
  return args.join('');
}

export type { PieChartSignature };
